/**
 * Canonical money formatting (audit p1.4).
 *
 * The repo had ~58 inline `Intl.NumberFormat(... currency ...)` call sites and
 * THREE divergent `formatPriceCompact` definitions (the two LP copies differed
 * only in their millions rounding branch). This is the single source; the
 * `ci:currency-format` gate ratchets new bare currency formatters toward zero.
 */
const USD0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const USD2 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Full price, rounded to the nearest $1,000 per brand voice ($895,000 not $894,750). */
export function formatPrice(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return USD0.format(Math.round(n / 1000) * 1000)
}

/**
 * Exact whole-dollar price (no thousand-rounding) for data documents where the
 * displayed figure must equal the source row (CMA comp grids, adjustment math
 * that has to foot). Marketing copy should prefer formatPrice.
 */
export function formatPriceExact(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return USD0.format(n)
}

/** Dollar-and-cent price for payment math (P&I), never thousand-rounded. */
export function formatPriceCents(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return USD2.format(n)
}

/** Compact price for tight UI: `$1.2M` / `$895K`. One agreed rounding rule. */
export function formatPriceCompact(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  const thousands = Math.round(n / 1_000)
  // $999,900 rounded to thousands is 1000 — never print $1000K.
  if (n >= 1_000_000 || thousands >= 1_000) {
    const m = n / 1_000_000
    return `$${m.toFixed(m >= 10 ? 0 : 1)}M`
  }
  if (n >= 1_000) return `$${thousands}K`
  return USD0.format(n)
}
